import './styles/app.css';
import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App as AntdApp, ConfigProvider } from 'antd';
import { useIntl } from 'react-intl';
import { ThemeProvider } from 'styled-components';

import { I18nProvider } from '@localization/intl-provider';
import { apiClient } from '@services/api/Api';
import { useVismaConfiguration } from '@services/visitor-management';
import { useUserStore } from '@store/use-user-store';
import { navigateTo } from '@utils/navigation';
import { ROUTES } from './constants/routes';

import Router from './Router';
import setupAxios, { isTokenExpired } from './services/api/setupAxios';
import defaultTheme from './theme/theme';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnMount: false,
      refetchInterval: false,
      refetchOnWindowFocus: false
    },
    mutations: { retry: false }
  }
});

setupAxios(apiClient);

function SessionObserver() {
  const { notification: antdNotification } = AntdApp.useApp();
  const intl = useIntl();

  useEffect(() => {
    const checkTokenExpiry = () => {
      const { accessToken, refreshToken } = useUserStore.getState();
      if (
        accessToken &&
        isTokenExpired(accessToken) &&
        refreshToken &&
        isTokenExpired(refreshToken)
      ) {
        navigateTo(ROUTES.LOGOUT);
        antdNotification.warning({
          message: intl.formatMessage({ id: 'SESSION_EXPIRED_TITLE' }),
          description: intl.formatMessage({ id: 'SESSION_EXPIRED_DESC' }),
          placement: 'topRight'
        });
      }
    };

    checkTokenExpiry();
    const interval = setInterval(checkTokenExpiry, 10000);
    return () => clearInterval(interval);
  }, []);

  return null;
}

const ThemedApp = () => {
  const { configuration } = useVismaConfiguration();
  const gi = configuration?.generalInformation;

  const theme = {
    ...defaultTheme,
    primary: gi?.color_primary ?? defaultTheme.primary,
    text: gi?.color_primary_text ?? defaultTheme.text,
    textSecondary: gi?.color_secondary_text ?? defaultTheme.textSecondary
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: theme.primary,
          colorError: theme.error,
          colorText: theme.text,
          colorTextSecondary: theme.textSecondary,
          colorBgContainer: theme.backgroundInput,
          colorBorder: theme.border,
          borderRadius: 9
        },
        components: {
          Checkbox: { controlInteractiveSize: 24, lineWidth: 2 }
        }
      }}
    >
      <AntdApp style={{ height: '100%' }}>
        <ThemeProvider theme={theme}>
          <I18nProvider>
            <SessionObserver />
            <Router />
          </I18nProvider>
        </ThemeProvider>
      </AntdApp>
    </ConfigProvider>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemedApp />
    </QueryClientProvider>
  );
}

export default App;
